<template>
  <SlideIn
    tabindex="-1"
    name="FilterSlideIn"
    role="dialog"
    aria-modal="true"
    aria-labelledby="filter-slidein-title"
    @open="setStateFromUrlParams"
  >
    <template #slideInHeader>
      <div
        class="sticky top-0 z-50 flex w-full items-center justify-between bg-white pb-2 pt-8 xl:relative xl:p-0"
      >
        <Headline
          id="filter-slidein-title"
          look="h2"
          tag="h1"
          tabindex="-1"
          role="heading"
          aria-level="1"
          data-testid="filter-slidein-title"
        >
          {{ $t('filter.title') }}
        </Headline>
        <FimButton
          type="tertiary"
          is-small
          data-testid="slide-in-close-button"
          :aria-label="$t('filter.close')"
          @click="toggle"
        >
          <template #iconAfter>
            <LazyIconFielmannClose class="size-6" aria-hidden="true" />
          </template>
        </FimButton>
      </div>
    </template>
    <template #slideInBody>
      <FilterGroup
        v-for="filter in availableFilters"
        :key="filter.slug"
        class="mt-10"
        :label="
          filter.name.toLowerCase() === 'prices'
            ? $t('filter.price')
            : filter.name
        "
        :badge="state?.[filter.slug]?.length || undefined"
        @click:reset="resetFilter(filter.slug)"
      >
        <GlassesWidthSlider
          v-if="filter.slug === 'glassesWidth'"
          v-model="glassesWidthState"
          :min="minMaxGlassesWidth[0]"
          :max="minMaxGlassesWidth[1]"
          :data="glassesWidthValues"
          data-testid="glasses-width-slider"
        />
        <FilterCategorySelect
          v-else-if="filter.slug === 'filterCategory'"
          v-model="state[filter.slug]"
          :filter="filter"
          data-testid="filter-category-select"
        />
        <template v-if="isGenericAttributeFilter(filter)">
          <fieldset
            :aria-labelledby="`${filter.slug}-legend`"
            class="mt-0 space-y-0"
            role="group"
            data-testid="generic-attribute-filter"
          >
            <legend :id="`${filter.slug}-legend`" class="sr-only">
              {{ filter.name }}
            </legend>
            <MultipleSelectionList
              v-model="state[filter.slug]"
              :items="filter.values"
              class="mt-2 space-y-2"
              tag="ul"
            >
              <template #item="{ item }: { item: any }">
                <li class="mb-[5px]">
                  <Checkbox
                    :id="filter.slug + '-' + item.value"
                    v-model="state[filter.slug]"
                    :label="
                      filter.slug === 'headWidth'
                        ? $t(`filter.head_width.${item.handle}`)
                        : item.displayName
                    "
                    :item="item"
                    text-size="md"
                    :input-data-test-id="`filter-${filter.slug}-${item.displayName}`"
                  >
                    <template v-if="filter.iconPath" #icon>
                      <div class="mr-2 flex min-w-[24px] justify-center">
                        <component
                          :is="resolveComponent('Lazy' + item.iconName)"
                          class="max-h-[40px] w-full max-w-[40px]"
                        />
                      </div>
                    </template>

                    <template #label>
                      <span class="tabular-nums">
                        {{
                          filter.slug === 'headWidth'
                            ? $t(`filter.head_width.${item.handle}`)
                            : item.displayName
                        }}
                      </span>
                      <span class="sr-only">
                        {{ filter.name }}
                      </span>
                    </template>
                  </Checkbox>
                </li>
              </template>
            </MultipleSelectionList>
          </fieldset>
        </template>

        <div v-else-if="filter.slug === 'prices'">
          <RangeSlider
            v-model="state.prices"
            :min="minPrice"
            :max="maxPrice"
            :value-formatter="toCurrencyWithoutFractionDigits"
            :min-label="$t('filter.minimal_price')"
            :max-label="$t('filter.maximal_price')"
            data-testid="range-slider"
          />
        </div>
        <!-- below not tested or used yet -->
        <template v-if="filter.type === 'boolean'" #action>
          <Toggle v-model="state[filter.slug]" />
        </template>
      </FilterGroup>
    </template>
    <template #slideInActions>
      <div class="flex flex-col gap-2 md:flex-row xl:flex-col">
        <FimButton
          type="tertiary"
          class="w-full"
          data-testid="reset-filter-button"
          @click="resetFilters"
          >{{ $t('filter.delete') }}
        </FimButton>
        <FimButton
          type="primary"
          class="w-full"
          :disabled="productCountData === 0 || !!productCountFetching"
          data-testid="filter-results-button"
          :aria-label="
            productCountData && !productCountFetching
              ? $t('filter.show_result') + ' ' + productCountData
              : ''
          "
          @click="applyFilters"
        >
          <div class="flex items-center justify-center gap-2">
            {{ $t('filter.show_result') }}
            <template v-if="!productCountFetching">
              ({{ productCountData }})
            </template>
            <template v-else>
              (<FimLoadingSpinner class="!h-4 !w-4" />)
            </template>
          </div>
        </FimButton>
        <span class="sr-only" aria-live="polite" aria-atomic="true">
          {{
            productCountData !== null && !productCountFetching
              ? $t('filter.product_count_announcement', {
                  count: productCountData,
                })
              : ''
          }}
        </span>
      </div>
    </template>
  </SlideIn>
</template>

<script setup lang="ts">
import type { FilterParams } from '@scayle/storefront-nuxt'
import type { FiltersEndpointResponseData } from '@scayle/storefront-api'
import { useFaceWidth } from '~/composables/useFaceWidth'
import { useEventListener } from '@vueuse/core'

type Props = {
  filters: FiltersEndpointResponseData
  refreshProductCount: ({ where }: FilterParams) => Promise<void>
  productCountData: number | undefined | null
  productCountFetching: boolean | null
}

const props = withDefaults(defineProps<Props>(), {
  filters: () => [],
  refreshProductCount: async () => {},
  productCountData: undefined,
  productCountFetching: true,
})

const { toggle, isOpen } = useSlideIn('FilterSlideIn')

const previousActiveElement = ref<HTMLElement | null>(null)

const handleEscKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isOpen.value) {
    toggle()
  }
}

useEventListener('keydown', handleEscKey)

watch(isOpen, (open) => {
  const mainContent = document.getElementById('home-page-content')

  if (open) {
    previousActiveElement.value = document.activeElement as HTMLElement
    mainContent?.setAttribute('aria-hidden', 'true')

    nextTick(() => {
      const titleElement = document.getElementById('filter-slidein-title')
      if (titleElement instanceof HTMLElement) {
        titleElement.focus()
      }
    })
  } else {
    mainContent?.removeAttribute('aria-hidden')

    nextTick(() => {
      previousActiveElement.value?.focus()
    })
  }
})

const {
  availableFilters,
  setStateFromUrlParams,
  resetFilter,
  glassesWidthState,
  glassesWidthValues,
  minMaxGlassesWidth,
  state,
  minPrice,
  maxPrice,
  toCurrencyWithoutFractionDigits,
  resetFilters,
  applyFilters,
  applyFiltersWithoutToggle,
  setFaceWidthFilter,
} = useFilter(props.filters, props.refreshProductCount)

const { faceWidth } = useFaceWidth()

onMounted(() => {
  setStateFromUrlParams()

  if (faceWidth.value) {
    setFaceWidthFilter(faceWidth.value)
    applyFiltersWithoutToggle()
  }
})

onBeforeUnmount(() => {
  resetFilters()
})

watch(faceWidth, (newWidth, oldWidth) => {
  if (newWidth && newWidth !== oldWidth) {
    oldWidth = newWidth
    setFaceWidthFilter(newWidth)
    applyFiltersWithoutToggle()
  }
})

const totalAppliedFilters = computed(() => {
  if (!state.value) return 0

  return Object.entries(state.value).reduce((total, [key, value]) => {
    // Skip glassesWidth if it's at default values
    if (key === 'glassesWidth') {
      const isDefault =
        value.length === 0 ||
        (glassesWidthState.value[0] === minMaxGlassesWidth.value[0] &&
          glassesWidthState.value[1] === minMaxGlassesWidth.value[1])
      return isDefault ? total : total + 1
    }

    // Skip prices if they're at default values
    if (key === 'prices') {
      const isDefault =
        Array.isArray(value) &&
        value[0] === minPrice.value &&
        value[1] === maxPrice.value
      return isDefault ? total : total + 1
    }

    return total + (Array.isArray(value) ? value.length : 0)
  }, 0)
})

const emit = defineEmits<{
  (e: 'update:appliedFiltersCount', count: number): void
}>()

watch(totalAppliedFilters, (newCount) => {
  emit('update:appliedFiltersCount', newCount)
})

const SPECIAL_FILTER_SLUGS = ['filterCategory', 'glassesWidth'] as const

const isGenericAttributeFilter = (filter: any) => {
  return (
    filter.type === 'attributes' && !SPECIAL_FILTER_SLUGS.includes(filter.slug)
  )
}
</script>
